import { hydraUiApi } from '@/services/api';
import { FEATURE_FLAG_DEFAULTS } from '@/services/auth/feature-flags';
import { isLocalDevHelperEnvironment } from './store';

/**
 * Hydra's registered defaults, keyed by plain string so an unregistered flag
 * (one that only exists in the `ui` repo, or only in Flightdeck) can be looked
 * up without widening {@link FEATURE_FLAG_DEFAULTS}.
 */
const defaultsByName = new Map<string, boolean>(Object.entries(FEATURE_FLAG_DEFAULTS));

/**
 * The account endpoints answer with the flags *evaluated* for the account, and
 * the backend stores nothing for a flag whose value already matches its own
 * default — so a hydra-only flag turned off is simply *absent* from the next
 * read rather than returned as `false`. Resolve absence the way the running app
 * does, through hydra's registered default, so a read-back never answers
 * `undefined` for a flag that was just written.
 */
function resolveFlag(flags: Record<string, boolean>, name: string): boolean {
  return flags[name] ?? defaultsByName.get(name) ?? false;
}

function resolveAccountId(): string {
  const accountId = window.__hydra__?.account?.id;
  if (accountId == null) {
    throw new Error('No account is loaded yet — wait for the app to finish booting, then try again.');
  }
  return String(accountId);
}

/** The account's flags as the backend evaluates them. */
async function readFlags(): Promise<Record<string, boolean>> {
  const response = await hydraUiApi.accounts.getFeatureFlags({
    params: { path: { account_id: resolveAccountId() } },
  });
  return response.featureFlags ?? {};
}

/**
 * Send only the flags being changed. The body is the bare map, not the
 * `{ featureFlags }` envelope the GET answers with — and it is an *overlay*:
 * the backend applies it on top of the account's evaluated flags, so a key left
 * out keeps whatever it had. Sending the read-back map instead would re-submit
 * every Flightdeck-registered flag, and each one in the body rewrites that
 * flag's shared account allowlist — turning a one-flag toggle into a write
 * across the whole flag table. The response is the account's flags after the
 * write, which is what the return value resolves from.
 */
async function writeFlags(changes: Record<string, boolean>): Promise<Record<string, boolean>> {
  return await hydraUiApi.accounts.updateFeatureFlags({
    params: { path: { account_id: resolveAccountId() } },
    body: changes,
  });
}

/**
 * Scriptable feature-flag control for local development, exposed as
 * `window.__hydra__.featureFlags`. The cmd+K commands and the dev helper's
 * Feature flags tab cover the same ground for a human; this exists for callers
 * that can only reach the page through the console — browser-driving agents,
 * and `page.evaluate`-style test harnesses.
 *
 * Every method resolves with the flag values as the app will read them, so a
 * write the backend declined to store reports its real result rather than
 * echoing the requested value. Flag changes reach the app on the next account
 * fetch: reload the page after writing.
 */
export const featureFlagDevTools = {
  /** Every flag hydra knows about plus everything the account resolves, resolved. */
  async list(): Promise<Record<string, boolean>> {
    const flags = await readFlags();
    const names = new Set([...defaultsByName.keys(), ...Object.keys(flags)]);
    return Object.fromEntries([...names].sort().map((name) => [name, resolveFlag(flags, name)]));
  },

  /** Read one flag. */
  async get(name: string): Promise<boolean> {
    return resolveFlag(await readFlags(), name);
  },

  /** Set one flag, leaving every other flag as it was. Resolves with the value after the write. */
  async set(name: string, enabled: boolean): Promise<boolean> {
    return resolveFlag(await writeFlags({ [name]: enabled }), name);
  },

  /** Set several flags in one request. Resolves with the values of those flags after the write. */
  async setMany(flags: Record<string, boolean>): Promise<Record<string, boolean>> {
    const written = await writeFlags(flags);
    return Object.fromEntries(Object.keys(flags).map((name) => [name, resolveFlag(written, name)]));
  },
};

export type FeatureFlagDevTools = typeof featureFlagDevTools;

/**
 * The helper is only attached where the endpoints behind it exist: the account
 * feature-flag routes are registered outside production and refuse the request
 * there.
 */
export function featureFlagDevToolsForEnv(): { featureFlags: FeatureFlagDevTools } | Record<string, never> {
  if (!isLocalDevHelperEnvironment()) return {};
  return { featureFlags: featureFlagDevTools };
}
